/**
 * Cliente HTTP — placeholder para Fase 1.
 *
 * Pendiente:
 *   - Generar tipos a partir del esquema OpenAPI del backend (cuando exista).
 *   - Manejo de errores tipado (códigos: PUNCH_ALREADY_OPEN, IP_NOT_ALLOWED, etc.).
 *   - Renovación silenciosa de sesión.
 *   - Idempotency-Key para POST /punch/* (anti doble-click).
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions extends RequestInit {
  idempotencyKey?: string;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { idempotencyKey, headers, ...rest } = options;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...headers,
    },
    ...rest,
  });

  if (!response.ok) {
    // Soportamos dos formatos de respuesta de error:
    //  - NestJS por defecto: { statusCode, message, error }  (message string o array)
    //  - Custom estructurado: { error: { code, message, details } }
    let payload: {
      message?: string | string[];
      error?: string | { code?: string; message?: string; details?: unknown };
    } = {};
    try {
      payload = await response.json();
    } catch {
      // Cuerpo no es JSON
    }

    const customError = typeof payload.error === 'object' ? payload.error : null;
    const code = customError?.code ?? 'UNKNOWN';
    const messageFromArray = Array.isArray(payload.message) ? payload.message[0] : undefined;
    const messageFromString = typeof payload.message === 'string' ? payload.message : undefined;
    const message =
      customError?.message ?? messageFromArray ?? messageFromString ?? `HTTP ${response.status}`;
    const details = customError?.details;
    throw new ApiError(response.status, code, message, details);
  }

  // Algunas respuestas son 204 sin cuerpo
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}
